/**
 * The plate disambiguation rule (ISSUE-902 R4). Two teams in one game must
 * never draw the same plate text, which is what "Washington St." and
 * "Washington" did on the NCAA FBS card: both carry the abbreviation `WAS`,
 * so both rows plated `WAS` and the two teams looked identical.
 *
 * The fix is a rule applied per game, never a per team data edit: when the two
 * abbreviations match, one side keeps its abbreviation and the other is
 * qualified with one letter taken from its own name. The qualified text is
 * capped at 4 characters because `ScoreRow`'s inline mark and `TeamMark` are
 * both fitted to a four character abbreviation (ISSUE-541, ISSUE-542), and a
 * longer string would re-open a defect two cards already paid to close. The
 * qualified side is checked against the side that keeps its abbreviation and
 * re-qualified if it still matches, so the two texts always differ.
 */

function normalize(abbreviation: string): string {
  return abbreviation.trim().toUpperCase();
}

function words(name: string): string[] {
  return name.split(/\s+/).filter((word) => word !== "");
}

function suffixFor(name: string): string {
  const nameWords = words(name);
  const lastWord = nameWords[nameWords.length - 1] ?? "";
  const letters = lastWord.replace(/[^A-Za-z]/g, "");
  return letters === "" ? "2" : letters.slice(0, 1).toUpperCase();
}

// `otherKey` is the side that keeps its abbreviation. The qualifier letter
// alone does not guarantee a different plate: a four character abbreviation is
// truncated to three before the letter is appended, so "Washington Huskies"
// and "Washington", both WASH, both came back "WASH". The numbered fallback the
// rule already uses for a letterless last word settles that case too, and "3"
// covers the one shape left, a letterless last word against an abbreviation
// that already reads "XXX2".
function qualify(
  team: { name: string; abbreviation: string },
  otherKey: string,
): string {
  const base = normalize(team.abbreviation).slice(0, 3);
  const lettered = `${base}${suffixFor(team.name)}`;
  if (lettered !== otherKey) return lettered;
  const numbered = `${base}2`;
  return numbered === otherKey ? `${base}3` : numbered;
}

export function resolvePlatePair(input: {
  away: { name: string; abbreviation: string };
  home: { name: string; abbreviation: string };
}): { awayText: string; homeText: string } {
  const awayKey = normalize(input.away.abbreviation);
  const homeKey = normalize(input.home.abbreviation);
  if (awayKey !== homeKey) {
    return {
      awayText: input.away.abbreviation,
      homeText: input.home.abbreviation,
    };
  }

  // The longer name is the one a reader can still tell apart once a letter is
  // added, so it takes the qualifier and the plainer name is left alone. A tie
  // goes to the away side, which is the first line on every score row.
  const isAwayQualified =
    words(input.away.name).length >= words(input.home.name).length;
  return isAwayQualified
    ? {
        awayText: qualify(input.away, homeKey),
        homeText: input.home.abbreviation,
      }
    : {
        awayText: input.away.abbreviation,
        homeText: qualify(input.home, awayKey),
      };
}
